import type { ChatAuth } from "../types"
import { bearer, chatBaseUrl, chatFetch } from "./helpers"

type RawChatAuth = {
  session_token: string
  handle: string
  expires_in: number
  retraction_window_hours: number
}

/**
 * Costanza uses a two-step credential model:
 *   1. Exchange the platform JWT for an opaque, short-lived session token
 *      (`postAuth`). The JWT never leaves this call.
 *   2. Use that session token as the bearer for everything else — including,
 *      separately, as the XMPP SASL PLAIN password in the XMPP agent.
 *
 * `POST /auth` — exchange the platform JWT for a Costanza session token.
 * @param platformJwt the active session's access token (`auth.getActiveSession()`).
 */
export const postAuth = (platformJwt: string, signal?: AbortSignal) =>
  chatFetch<ChatAuth, "json">({
    baseUrl: chatBaseUrl(),
    path: "/auth",
    method: "POST",
    headers: bearer(platformJwt),
    responseType: "json",
    transform: (data) => {
      const raw = data as RawChatAuth
      return {
        sessionToken: raw.session_token,
        handle: raw.handle,
        expiresIn: raw.expires_in,
        // Falls back to costanza's own default. A server too old to send it is
        // configured with 24 anyway, and offering nothing would read as "you
        // can never delete a message" rather than as a version mismatch.
        retractionWindowHours: raw.retraction_window_hours ?? 24,
      }
    },
    signal,
  })
